import { useEffect, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router'
import { LoopApiError, type InviteAcceptInfo } from '@innerloopco/loopcms-core'
import { Button, Field, Input } from '../../components/index.js'
import { api } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import { PASSWORD_TOO_SHORT, PASSWORDS_DIFFER, SignedOutLayout } from './SignedOutLayout.js'

type Lookup = { state: 'loading' } | { state: 'dead' } | { state: 'ok'; invite: InviteAcceptInfo }

export function AcceptInvite() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const { adopt } = useAuth()
  const [lookup, setLookup] = useState<Lookup>({ state: 'loading' })
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!token) {
      setLookup({ state: 'dead' })
      return
    }
    api
      .getInvite(token)
      .then((invite) => { if (!cancelled) setLookup({ state: 'ok', invite }) })
      .catch(() => { if (!cancelled) setLookup({ state: 'dead' }) })
    return () => { cancelled = true }
  }, [token])

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (password.length < 8) {
      setError(PASSWORD_TOO_SHORT)
      return
    }
    if (password !== confirm) {
      setError(PASSWORDS_DIFFER)
      return
    }
    setBusy(true)
    setError(null)
    api
      .acceptInvite(token, name.trim(), password)
      .then(({ user }) => adopt(user))
      .catch((caught: unknown) => {
        setError(caught instanceof LoopApiError ? caught.message : 'Something went wrong. Try again.')
        setBusy(false)
      })
  }

  if (lookup.state === 'loading') return <SignedOutLayout title="Join LoopCMS"><p className="text-muted" style={{ margin: 0, fontSize: 13 }}>Checking your invite…</p></SignedOutLayout>

  if (lookup.state === 'dead') {
    return (
      <SignedOutLayout title="Join LoopCMS">
        <p role="alert" style={{ margin: 0, fontSize: 13 }}>
          This invite link is no longer valid. Ask whoever invited you to send a new one.
        </p>
        <Link to="/" style={{ fontSize: 13 }}>Go to sign in</Link>
      </SignedOutLayout>
    )
  }

  const { invite } = lookup
  return (
    <SignedOutLayout title="Join LoopCMS">
      <form className="stack" style={{ gap: 'var(--space-4)' }} onSubmit={onSubmit}>
        <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
          {invite.invitedByName ?? 'Someone'} invited you as {invite.role}.
        </p>
        <Field label="Email">
          <Input type="email" value={invite.email} readOnly autoComplete="username" />
        </Field>
        <Field label="Name">
          <Input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" autoFocus required />
        </Field>
        <Field label="Password" help={PASSWORD_TOO_SHORT}>
          <Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" />
        </Field>
        <Field label="Confirm password" error={error ?? undefined}>
          <Input type="password" value={confirm} onChange={(event) => setConfirm(event.target.value)} autoComplete="new-password" />
        </Field>
        <Button type="submit" variant="primary" block disabled={busy || !name.trim()} style={{ minHeight: 42 }}>
          {busy ? 'Creating…' : 'Create account'}
        </Button>
        <p className="text-muted" style={{ fontSize: 12, margin: 0 }}>You can turn on two-factor from your account page.</p>
      </form>
    </SignedOutLayout>
  )
}
